/**
 * Feature flags for the current utility, resolved by the Flagsmith SDK.
 *
 * Enum VALUES are the flag names in Flagsmith and follow
 * `ff-{group}-{feature}-{role}`; the member names are the app-facing handles. Only the
 * values ever change when a flag is renamed — call sites use the members.
 */
export enum FeatureFlagName {
	// Sidebar (Home is not gated — every tenant has it)
	SUPPORT = "ff-supportDesk-support-customer",
	// Dashboard quick services
	SERVICE_REQUEST = "ff-energyService-serviceRequest-customer",
	BUY_ELECTRICITY = "ff-buyingSharingPower-buyElectricity-customer",
	SAVED_BENEFICIARIES = "ff-buyingSharingPower-savedBeneficiaries-customer",
	TRANSFER_UNIT = "ff-buyingSharingPower-transferUnit-customer",
	BULK_ELECTRICITY_PURCHASE = "ff-buyingSharingPower-bulkElectricityPurchase-customer",
	OUTSTANDING_DEBT_DISPLAY = "ff-buyingSharingPower-outstandingDebtDisplay-customer",
	// Account activities — the page is reachable only when STATEMENT_OF_ACCOUNT is on;
	// HISTORICAL_PAYMENT only gates its Historical Payment option
	STATEMENT_OF_ACCOUNT = "ff-buyingSharingPower-statementAndHistory-customer",
	HISTORICAL_PAYMENT = "ff-buyingSharingPower-paymentAndOrderHistory-customer",
	// Page-level sub-features
	NIN_VERIFICATION = "ff-buyingSharingPower-ninVerification-customer",
	CASH_PAYMENT = "ff-payment-payWithCash-customer",
	UNIT_TRESHOLD_REMINDER = "ff-energyService-unitThresholdReminder-customer",
	REMOTE_TOKEN_LOAD = "ff-buyingSharingPower-remoteTokenLoad-customer",
	// -system rather than -customer, because the backend already names it that way in
	// Gateway's FeatureFlagNames. Renaming it is a coordinated change, not a merge fix.
	ASK_A_FRIEND_TO_PAY = "ff-payment-askAFriendToPay-system",
	// Guest bill payment page; -system for the same reason (Gateway's GuestBillingPolicy)
	GUEST_BILLING = "ff-vending-guestBilling-system",

	// Utility admin — Bills Payment & Operations module. The module card on the
	// util-admin home locks when every flag in the group is off.
	ADMIN_VENDING_HISTORY = "ff-billsPaymentOperations-vendingHistory-admin",
	ADMIN_POSTPAID_HISTORY = "ff-billsPaymentOperations-postpaidHistory-admin",
	ADMIN_BULK_PURCHASES = "ff-billsPaymentOperations-bulkPurchases-admin",
	ADMIN_CUSTOMERS = "ff-billsPaymentOperations-customers-admin",
	ADMIN_BROADCAST = "ff-billsPaymentOperations-broadcast-admin",
	ADMIN_DISTRICT_REVENUE_ANALYTICS = "ff-billsPaymentOperations-districtRevenueAnalytics-admin",
	ADMIN_REFUNDS = "ff-billsPaymentOperations-refunds-admin",
	// Utility admin — Service Request module
	ADMIN_REFERRAL_SYSTEM = "ff-serviceRequest-referralSystem-admin",
	ADMIN_AUDIT_LOG = "ff-serviceRequest-auditLog-admin",
	ADMIN_SERVICE_REQUEST_SETTINGS = "ff-serviceRequest-settings-admin",
	ADMIN_SERVICE_REQUEST_WITH_DEPARTMENT = "ff-serviceRequest-serviceRequestWithDepartment-admin",
	// Utility admin — Support Desk module
	ADMIN_CREATE_SERVICE_REQUEST = "ff-supportDesk-createServiceRequest-admin",
	ADMIN_CUSTOMER_MANAGEMENT = "ff-supportDesk-customerManagement-admin",
	ADMIN_DEPARTMENT_ROUTING = "ff-supportDesk-departmentRouting-admin",
	// Utility admin — Roles Management
	ADMIN_CREATE_AND_ASSIGN_ROLES = "ff-rolesManagement-createAndAssignRoles-admin",
	ADMIN_VIEW_PERMISSIONS = "ff-rolesManagement-viewPermissions-admin",
	ADMIN_EDIT_PERMISSIONS = "ff-rolesManagement-editPermissions-admin",
	ADMIN_INTEGRATION_ACCESS_CONTROL = "ff-rolesManagement-integrationAccessControl-admin",
}

/**
 * Admin flags per util-admin module, in Feature Configuration order. A module is
 * reachable while at least one of its flags is on; all off locks the module card.
 */
export const BILLS_PAYMENT_OPERATIONS_ADMIN_FLAGS: readonly FeatureFlagName[] = [
	FeatureFlagName.ADMIN_VENDING_HISTORY,
	FeatureFlagName.ADMIN_POSTPAID_HISTORY,
	FeatureFlagName.ADMIN_BULK_PURCHASES,
	FeatureFlagName.ADMIN_CUSTOMERS,
	FeatureFlagName.ADMIN_BROADCAST,
	FeatureFlagName.ADMIN_DISTRICT_REVENUE_ANALYTICS,
	FeatureFlagName.ADMIN_REFUNDS,
];

export const SERVICE_REQUEST_ADMIN_FLAGS: readonly FeatureFlagName[] = [
	FeatureFlagName.ADMIN_REFERRAL_SYSTEM,
	FeatureFlagName.ADMIN_AUDIT_LOG,
	FeatureFlagName.ADMIN_SERVICE_REQUEST_SETTINGS,
	FeatureFlagName.ADMIN_SERVICE_REQUEST_WITH_DEPARTMENT,
];

export const SUPPORT_DESK_ADMIN_FLAGS: readonly FeatureFlagName[] = [
	FeatureFlagName.ADMIN_CREATE_SERVICE_REQUEST,
	FeatureFlagName.ADMIN_CUSTOMER_MANAGEMENT,
	FeatureFlagName.ADMIN_DEPARTMENT_ROUTING,
];

export const ROLES_MANAGEMENT_ADMIN_FLAGS: readonly FeatureFlagName[] = [
	FeatureFlagName.ADMIN_CREATE_AND_ASSIGN_ROLES,
	FeatureFlagName.ADMIN_VIEW_PERMISSIONS,
	FeatureFlagName.ADMIN_EDIT_PERMISSIONS,
	FeatureFlagName.ADMIN_INTEGRATION_ACCESS_CONTROL,
];

/** Flat map of feature → enabled for the current utility. */
export type FeatureFlags = Record<string, boolean>;

/**
 * Is `flag` enabled? Unknown flag / no data → false (safe default: a feature stays
 * off until the backend explicitly enables it).
 */
export function isFeatureEnabled(flags: FeatureFlags | undefined, flag: string): boolean {
	return flags?.[flag] ?? false;
}
